/**
 * OrderPage (Экран заказа) — full detail of a single order plus its realtime chat.
 *
 * Shows the order number, status, server-computed totals, snapshotted line items and
 * the currency that was spent. The {@link OrderChat} widget streams live messages over
 * the order's WebSocket and offers "Позвать продавца" + the 10-minute SLA hint.
 *
 * All amounts are rendered straight from the server payload (snapshots in ``items``);
 * nothing is recomputed on the client.
 */
import { useRef, useState } from "react";

import { Price } from "@melusine/shared-ui";
import { useBackButton } from "@melusine/shared-telegram";
import { OrderStatusBadge, transitionOrder, useOrder } from "@entities/order";
import { useMe } from "@entities/user";
import { BotActivationBanner } from "@widgets/bot-activation";
import { OrderCancelControl, useSelfCancel } from "@features/order-cancel";
import { ReceiptControl } from "@features/order-receipt";
import { ReviewSheet } from "@features/review";
import { TipSheet } from "@features/tip";
import { OrderChat } from "@widgets/order-chat";
import { StatePanel } from "@widgets/catalog-browser";
import { routes, useNavigate } from "@shared/router";

import styles from "./OrderPage.module.css";

export interface OrderPageProps {
  readonly orderId: number;
}

function itemsLabel(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return "товар";
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return "товара";
  }
  return "товаров";
}

export function OrderPage({ orderId }: OrderPageProps): JSX.Element {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: order, isLoading, error, refetch } = useOrder(orderId);
  const [reviewOpen, setReviewOpen] = useState(false);
  // «Перевод выполнен» (declare_payment) — anti-double-click via a ref guard + flag (ТЗ №2доп).
  const [isDeclaring, setIsDeclaring] = useState(false);
  const declaringRef = useRef(false);

  useBackButton(() => {
    navigate(routes.orders());
  });

  const declarePayment = (): void => {
    if (declaringRef.current) {
      return;
    }
    declaringRef.current = true;
    setIsDeclaring(true);
    transitionOrder(orderId, "declare_payment")
      .then(() => {
        refetch();
      })
      .finally(() => {
        declaringRef.current = false;
        setIsDeclaring(false);
      });
  };

  // Self-cancel button + live countdown (ТЗ §11). Called unconditionally (before the early
  // returns) with safe fallbacks while the order loads; it renders nothing until relevant.
  const selfCancel = useSelfCancel({
    orderId,
    status: order?.status ?? "created",
    canSelfCancel: order?.can_self_cancel ?? false,
    secondsLeft: order?.self_cancel_seconds_left ?? 0,
    onCancelled: refetch,
  });

  if (isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (error || !order) {
    return (
      <StatePanel
        kind="error"
        message="Заказ не найден или недоступен."
        onRetry={refetch}
      />
    );
  }

  const itemCount = order.items.reduce((sum, item) => sum + item.qty, 0);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.back}
          aria-label="Назад"
          onClick={() => navigate(routes.orders())}
        >
          <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
        </button>
        <div className={styles.headMain}>
          <h1 className={styles.number}>Заказ {order.number}</h1>
          <span className={styles.subline}>
            {itemCount} {itemsLabel(itemCount)} ·{" "}
            {Number(order.total_rub).toLocaleString("ru-RU")} ₽
          </span>
        </div>
        <OrderStatusBadge status={order.status} />
      </header>

      {me ? <BotActivationBanner me={me} startParam={`order_${order.id}`} /> : null}

      <OrderChat
        orderId={order.id}
        currentUserId={me?.id ?? null}
        chatPostable={order.chat_postable}
        summary={
          <section className={styles.summary}>
            <ul className={styles.items}>
              {order.items.map((item) => (
                <li key={item.id} className={styles.item}>
                  <span className={styles.itemTitle}>
                    {item.title_snapshot}
                    {item.qty > 1 ? ` ×${item.qty}` : ""}
                  </span>
                  <Price amount={item.line_total} size="sm" />
                </li>
              ))}
            </ul>

            {order.user_comment ? (
              <p className={styles.comment}>{order.user_comment}</p>
            ) : null}

            <div className={styles.totals}>
              <Row label="Сумма" value={order.subtotal} />
              {Number(order.primogem_spent_rub) > 0 ? (
                <Row label="Примогемы" value={`-${order.primogem_spent_rub}`} discount />
              ) : null}
              {Number(order.star_glitter_spent_rub) > 0 ? (
                <Row
                  label="Звёздный блеск"
                  value={`-${order.star_glitter_spent_rub}`}
                  discount
                />
              ) : null}
              <div className={styles.grandRow}>
                <span className={styles.grandLabel}>Итого</span>
                <Price amount={order.total_rub} accent size="lg" />
              </div>
            </div>

            {order.status === "awaiting_payment" &&
            order.available_transitions.includes("declare_payment") ? (
              <div className={styles.payDeclare}>
                <p className={styles.payDeclareText}>
                  Оплатили заказ? Сообщите продавцу, чтобы он проверил перевод.
                </p>
                <button
                  type="button"
                  className={styles.payDeclareButton}
                  onClick={declarePayment}
                  disabled={isDeclaring}
                >
                  {isDeclaring ? "Отправляем…" : "Перевод выполнен"}
                </button>
              </div>
            ) : null}

            <OrderCancelControl state={selfCancel} variant="panel" />

            <ReceiptControl
              orderId={order.id}
              status={order.status}
              canRequest={order.can_request_receipt}
              state={order.receipt_request_state}
              onRequested={refetch}
              onOpenSupport={() => navigate(routes.support())}
            />

            {order.status === "completed" ? <TipSheet orderId={order.id} /> : null}

            {order.can_review ? (
              <div className={styles.reviewCta}>
                <p className={styles.reviewCtaText}>
                  Заказ выполнен. Поделитесь впечатлениями о покупке.
                </p>
                <button
                  type="button"
                  className={styles.reviewCtaButton}
                  onClick={() => setReviewOpen(true)}
                >
                  Оставить отзыв
                </button>
              </div>
            ) : null}
          </section>
        }
      />

      {order.can_review ? (
        <ReviewSheet
          open={reviewOpen}
          orderId={order.id}
          orderNumber={order.number}
          {...(order.items[0]?.title_snapshot !== undefined
            ? { productTitle: order.items[0].title_snapshot }
            : {})}
          createdAt={order.created_at}
          onClose={() => setReviewOpen(false)}
          onSubmitted={() => {
            setReviewOpen(false);
            refetch();
          }}
        />
      ) : null}
    </div>
  );
}

interface RowProps {
  readonly label: string;
  readonly value: string;
  readonly discount?: boolean;
}

function Row({ label, value, discount = false }: RowProps): JSX.Element {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <Price amount={value} size="sm" {...(discount ? { className: styles.discount } : {})} />
    </div>
  );
}
